import React, { useEffect, useState } from 'react';
import { Header } from '../../components/layout/Header';
import { KPICard } from '../../components/dashboard/KPICard';
import { TrendChart, BarChartComponent, PieChartComponent } from '../../components/dashboard/Charts';
import { reportsApi } from '../../lib/api';
import { toast } from 'sonner';
import { formatCurrency, formatDateRange } from '../../lib/utils';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { 
    Megaphone, Target, Eye, MousePointer,
    Percent, Users, DollarSign, TrendingUp, Calendar
} from 'lucide-react';

export default function AdsPage() {
    const [loading, setLoading] = useState(true);
    const [reports, setReports] = useState([]);
    const [selectedReportId, setSelectedReportId] = useState('');
    const [hasData, setHasData] = useState(false);

    useEffect(() => { loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadData = async () => {
        try {
            const response = await reportsApi.getAll();
            setReports(response.data);
            if (response.data.length > 0) {
                setSelectedReportId(response.data[0].id);
                setHasData(true);
            }
        } catch (error) {
            toast.error('Failed to load ads data');
        } finally {
            setLoading(false);
        }
    };

    const selectedReport = reports.find(r => r.id === selectedReportId);
    const data = selectedReport?.ads || {};

    const trendData = reports.slice(0, 8).reverse().map((report, index) => ({
        name: `W${index + 1}`,
        impressions: Math.round((report.ads?.impressions || 0) / 1000),
        clicks: report.ads?.clicks || 0,
    }));

    const industryData = Object.entries(data?.industry_breakdown || {}).map(([name, value]) => ({ name, value }));
    const tierData = Object.entries(data?.tier_breakdown || {}).map(([name, value]) => ({ name, accounts: value }));

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-channel-ads/30 border-t-channel-ads rounded-full animate-spin" />
            </div>
        );
    }

    if (!hasData) {
        return (
            <div className="min-h-screen" data-testid="ads-page">
                <Header title="Account-Based Advertising" subtitle="Ad campaign metrics and account reach analytics" />
                <div className="p-8">
                    <div className="card-base p-12 text-center">
                        <Megaphone className="w-16 h-16 text-content-muted mx-auto mb-4" />
                        <h3 className="text-xl font-heading font-bold text-white mb-2">No Ads Data Available</h3>
                        <p className="text-content-secondary max-w-md mx-auto">Advertising metrics will appear here once your admin team adds report data.</p>
                    </div>
                </div>
            </div>
        );
    }

    const ads = data;

    return (
        <div className="min-h-screen" data-testid="ads-page">
            <Header title="Account-Based Advertising" subtitle="Ad campaign metrics and account reach analytics" />

            <div className="p-8 space-y-8">
                {/* Week Selector */}
                <div className="flex items-center gap-4" data-testid="ads-week-selector">
                    <Calendar className="w-5 h-5 text-content-muted" />
                    <Select value={selectedReportId} onValueChange={setSelectedReportId}>
                        <SelectTrigger className="input-base w-80">
                            <SelectValue placeholder="Select period" />
                        </SelectTrigger>
                        <SelectContent className="bg-surface-card border-line-default max-h-60">
                            {reports.map((r) => (
                                <SelectItem key={r.id} value={r.id}>
                                    {formatDateRange(r.period_start, r.period_end)} ({r.report_type})
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* KPI Cards */}
                <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <KPICard title="Accounts Targeted" value={ads.accounts_targeted || 0} icon={Target} color="ads" data-testid="ads-accounts-kpi" />
                    <KPICard title="Impressions" value={ads.impressions || 0} icon={Eye} data-testid="ads-impressions-kpi" />
                    <KPICard title="Clicks" value={ads.clicks || 0} icon={MousePointer} data-testid="ads-clicks-kpi" />
                    <KPICard title="CTR" value={`${(ads.ctr || 0).toFixed(2)}%`} icon={Percent} color="red" data-testid="ads-ctr-kpi" />
                </section>
                <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <KPICard title="Account Reach %" value={`${(ads.account_reach_percent || 0).toFixed(1)}%`} icon={Users} data-testid="ads-reach-kpi" />
                    <KPICard title="Engagement Rate" value={`${(ads.engagement_rate || 0).toFixed(1)}%`} icon={TrendingUp} data-testid="ads-engagement-kpi" />
                    <KPICard title="Cost/Engaged Acct" value={formatCurrency(ads.cost_per_engaged_account || 0)} icon={DollarSign} data-testid="ads-cost-engaged-kpi" />
                    <KPICard title="Cost/MQL" value={formatCurrency(ads.cost_per_mql || 0)} icon={DollarSign} color="red" data-testid="ads-cost-mql-kpi" />
                </section>

                {/* Influenced Pipeline */}
                <section className="card-base p-6" data-testid="ads-pipeline">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-heading font-bold text-white">INFLUENCED PIPELINE</h3>
                        <div className="text-3xl font-heading font-bold text-brand-red">{formatCurrency(ads.influenced_pipeline || 0)}</div>
                    </div>
                    <div className="h-4 bg-surface-app rounded-full overflow-hidden">
                        <div className="h-full rounded-full bg-gradient-to-r from-brand-red to-channel-ads" style={{ width: `${ads.account_reach_percent || 0}%` }} />
                    </div>
                    <p className="text-sm text-content-muted mt-2">{(ads.account_reach_percent || 0).toFixed(1)}% of target pipeline influenced through account-based advertising</p>
                </section>

                {/* Charts */}
                {trendData.some(d => d.impressions > 0 || d.clicks > 0) && (
                    <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <TrendChart data={trendData} dataKey="impressions" title="IMPRESSIONS TREND (K)" color="#FBBC05" />
                        <TrendChart data={trendData} dataKey="clicks" title="CLICKS TREND" color="#FF3B30" />
                    </section>
                )}
                {(industryData.length > 0 || tierData.length > 0) && (
                    <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {industryData.length > 0 && <PieChartComponent data={industryData} title="INDUSTRY DISTRIBUTION" />}
                        {tierData.length > 0 && <BarChartComponent data={tierData} dataKeys={['accounts']} title="ACCOUNT TIER BREAKDOWN" />}
                    </section>
                )}
            </div>
        </div>
    );
}
